import { useEffect, useState, useCallback } from "react";
import { useParams, Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import {
  ArrowLeft,
  Building2,
  Mail,
  Phone,
  MapPin,
  Globe,
  CreditCard,
  Loader2,
  FolderKanban,
  Tag,
  Calendar,
  Info,
  Percent,
  Euro,
  FileText,
  ExternalLink,
  Copy,
  CheckCircle2,
  Clock,
  XCircle,
  Receipt,
  UserCheck,
  TrendingUp,
  X,
  History,
  UserPlus,
  Landmark,
  Lock,
  Eye,
  EyeOff,
  Trash2,
  RefreshCw,
  Pause,
  Play,
  Ban,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { loadStripe } from "@stripe/stripe-js";
import { Elements, CardElement, IbanElement, useStripe, useElements } from "@stripe/react-stripe-js";

import { useAuth } from "@/contexts/AuthContext";
import SocialConnectionsWidget from "@/components/SocialConnectionsWidget";
import SecureVault from "@/components/SecureVault";

const API = `${(process.env.REACT_APP_BACKEND_URL || window.location.origin)}/api`;

const TABS = [
  { key: "info", label: "Informations", icon: Info },
  { key: "vault", label: "Coffre-fort", icon: Lock },
  { key: "pricing", label: "Tarification", icon: CreditCard },
  { key: "payments", label: "Paiements", icon: Receipt },
  { key: "social", label: "Reseaux Sociaux", icon: Globe },
];

function ClientAccountSection({ client, onClientUpdate }) {
  const [accountInfo, setAccountInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [email, setEmail] = useState(client.contact_email || "");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`${API}/clients/${client.id}/has-account`).then(res => {
      setAccountInfo(res.data);
    }).catch(() => {}).finally(() => setLoading(false));
  }, [client.id]);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!email || !password) { toast.error("Email et mot de passe requis"); return; }
    if (password.length < 6) { toast.error("Mot de passe: 6 caracteres minimum"); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/clients/${client.id}/create-account`, { email, password });
      toast.success("Compte client cree ! Email d'acces envoye.");
      setShowForm(false);
      setPassword("");
      const res = await axios.get(`${API}/clients/${client.id}/has-account`);
      setAccountInfo(res.data);
      if (onClientUpdate) onClientUpdate();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur creation compte");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return null;

  return (
    <div className="bg-[#151B2B] border border-[#2A3441] rounded-xl p-6" data-testid="client-account-section">
      <h3 className="text-white font-semibold flex items-center gap-2 mb-4">
        <UserPlus className="h-4 w-4 text-[#0099CC]" />
        Acces portail client
      </h3>

      {accountInfo?.has_account ? (
        <div data-testid="account-exists-info">
          <div className="flex items-center gap-3 bg-[#10B981]/5 border border-[#10B981]/20 rounded-lg p-4">
            <CheckCircle2 className="h-5 w-5 text-[#10B981] flex-shrink-0" />
            <div className="flex-1">
              <p className="text-white text-sm font-medium">Compte actif</p>
              <p className="text-[#94A3B8] text-xs">{accountInfo.email}</p>
            </div>
          </div>
          <Button
            onClick={async () => {
              try {
                await axios.post(`${API}/admin/clients/${client.id}/reset-password`);
                toast.success("Nouveau mot de passe genere et envoye par email au client !");
              } catch (err) {
                toast.error(err.response?.data?.detail || "Erreur lors de la reinitialisation");
              }
            }}
            variant="outline"
            size="sm"
            className="mt-3 border-[#F59E0B]/30 text-[#F59E0B] hover:bg-[#F59E0B]/10"
            data-testid="reset-client-password-btn"
          >
            <Lock className="h-4 w-4 mr-2" />
            Reinitialiser le mot de passe
          </Button>
        </div>
      ) : (
        <>
          {!showForm ? (
            <div className="text-center py-4">
              <p className="text-[#94A3B8] text-sm mb-3">Ce client n'a pas encore d'acces au portail.</p>
              <Button
                onClick={() => setShowForm(true)}
                className="bg-[#0099CC] hover:bg-[#0088B5] text-white"
                data-testid="create-account-btn"
              >
                <UserPlus className="h-4 w-4 mr-2" />
                Creer un compte client
              </Button>
            </div>
          ) : (
            <form onSubmit={handleCreate} className="space-y-4" data-testid="create-account-form">
              <div className="space-y-1.5">
                <Label className="text-[#94A3B8] text-xs">Email du client</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#64748b]" />
                  <Input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="client@exemple.com"
                    className="pl-10 bg-[#0B0F19] border-[#2A3441] text-white"
                    data-testid="account-email-input"
                  />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label className="text-[#94A3B8] text-xs">Mot de passe initial</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#64748b]" />
                  <Input
                    type={showPw ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Minimum 6 caracteres"
                    className="pl-10 pr-10 bg-[#0B0F19] border-[#2A3441] text-white"
                    data-testid="account-password-input"
                  />
                  <button type="button" onClick={() => setShowPw(!showPw)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[#64748b] hover:text-white">
                    {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
              <p className="text-[#64748b] text-xs">Un email avec les identifiants sera envoye au client.</p>
              <div className="flex gap-2">
                <Button type="submit" disabled={saving} className="bg-[#0099CC] hover:bg-[#0088B5] text-white" data-testid="confirm-create-account-btn">
                  {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <UserPlus className="h-4 w-4 mr-2" />}
                  Creer le compte
                </Button>
                <Button type="button" variant="outline" onClick={() => setShowForm(false)}
                  className="border-[#2A3441] text-[#94A3B8]">
                  Annuler
                </Button>
              </div>
            </form>
          )}
        </>
      )}
    </div>
  );
}

function ClientInfoTab({ client, onClientUpdate }) {
  const [affiliates, setAffiliates] = useState([]);
  const [selectedAffiliate, setSelectedAffiliate] = useState(client.affiliate_id || "");
  const [savingAffiliate, setSavingAffiliate] = useState(false);
  const [clientType, setClientType] = useState(client.client_type || "standard");
  const [savingType, setSavingType] = useState(false);

  useEffect(() => {
    axios.get(`${API}/affiliates`).then(res => {
      setAffiliates((res.data || []).filter(a => a.status === "active"));
    }).catch(() => {});
  }, []);

  const assignAffiliate = async () => {
    setSavingAffiliate(true);
    try {
      await axios.post(`${API}/clients/${client.id}/affiliate`, { affiliate_id: selectedAffiliate || null });
      toast.success(selectedAffiliate ? "Affilie attribue" : "Affilie retire");
      if (onClientUpdate) onClientUpdate();
    } catch { toast.error("Erreur attribution affilie"); }
    finally { setSavingAffiliate(false); }
  };

  const handleClientTypeChange = async (newType) => {
    setSavingType(true);
    try {
      await axios.put(`${API}/clients/${client.id}`, { client_type: newType });
      setClientType(newType);
      toast.success(`Type client mis a jour: ${newType === "agency" ? "Agence" : "Standard"}`);
      if (onClientUpdate) onClientUpdate();
    } catch { toast.error("Erreur mise a jour type client"); }
    finally { setSavingType(false); }
  };

  const currentAffiliate = affiliates.find(a => a.id === client.affiliate_id);

  return (
    <div className="space-y-6" data-testid="client-info-tab">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-[#151B2B] border border-[#2A3441] rounded-xl p-6 space-y-4">
          <h3 className="text-white font-semibold flex items-center gap-2">
            <Building2 className="h-4 w-4 text-[#0099CC]" />
            Entreprise
          </h3>
          <div className="space-y-3">
            <div>
              <p className="text-xs text-[#64748b] uppercase tracking-wider">Nom</p>
              <p className="text-white mt-0.5">{client.company_name}</p>
            </div>
            <div>
              <p className="text-xs text-[#64748b] uppercase tracking-wider">Secteur</p>
              <p className="text-[#0099CC] mt-0.5">{client.sector}</p>
            </div>
            {client.zone_intervention?.length > 0 && (
              <div>
                <p className="text-xs text-[#64748b] uppercase tracking-wider">Zone d'intervention</p>
                <div className="flex flex-wrap gap-1 mt-1">
                  {client.zone_intervention.map((z, i) => (
                    <span key={i} className="text-xs bg-[#2A3441] text-[#94A3B8] px-2 py-0.5 rounded">{z}</span>
                  ))}
                </div>
              </div>
            )}
          </div>
          {client.website_url && (
            <a
              href={client.website_url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 mt-2 px-4 py-2 bg-[#0099CC]/10 border border-[#0099CC]/30 text-[#0099CC] rounded-lg hover:bg-[#0099CC]/20 transition-colors text-sm font-medium"
              data-testid="view-vitrine-btn"
            >
              <ExternalLink className="h-4 w-4" />
              Voir la vitrine
            </a>
          )}

          {/* Client Type */}
          <div className="pt-4 border-t border-[#2A3441]">
            <p className="text-xs text-[#64748b] uppercase tracking-wider mb-2">Type de client</p>
            <div className="flex items-center gap-2">
              <button
                onClick={() => handleClientTypeChange("standard")}
                disabled={savingType}
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                  clientType === "standard"
                    ? "bg-[#64748b]/30 text-white border border-[#64748b]"
                    : "bg-[#151B2B] text-[#64748b] border border-[#2A3441] hover:border-[#64748b]"
                }`}
                data-testid="client-type-standard-btn"
              >
                Standard
              </button>
              <button
                onClick={() => handleClientTypeChange("agency")}
                disabled={savingType}
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                  clientType === "agency"
                    ? "bg-[#F59E0B]/30 text-[#F59E0B] border border-[#F59E0B]"
                    : "bg-[#151B2B] text-[#64748b] border border-[#2A3441] hover:border-[#F59E0B]"
                }`}
                data-testid="client-type-agency-btn"
              >
                Agence
              </button>
              {savingType && <Loader2 className="h-4 w-4 animate-spin text-[#0099CC]" />}
            </div>
          </div>
        </div>

        <div className="bg-[#151B2B] border border-[#2A3441] rounded-xl p-6 space-y-4">
          <h3 className="text-white font-semibold flex items-center gap-2">
            <Mail className="h-4 w-4 text-[#0099CC]" />
            Contact
          </h3>
          <div className="space-y-3">
            {client.contact_email && (
              <div className="flex items-center gap-2 text-[#94A3B8]">
                <Mail className="h-4 w-4 text-[#64748b] flex-shrink-0" />
                <span>{client.contact_email}</span>
              </div>
            )}
            {client.phone && (
              <div className="flex items-center gap-2 text-[#94A3B8]">
                <Phone className="h-4 w-4 text-[#64748b] flex-shrink-0" />
                <span>{client.phone}</span>
              </div>
            )}
            {client.city && (
              <div className="flex items-center gap-2 text-[#94A3B8]">
                <MapPin className="h-4 w-4 text-[#64748b] flex-shrink-0" />
                <span>{client.city}</span>
              </div>
            )}
            {client.address && (
              <div className="flex items-center gap-2 text-[#94A3B8]">
                <Globe className="h-4 w-4 text-[#64748b] flex-shrink-0" />
                <span>{client.address}</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Affiliate Assignment */}
      <div className="bg-[#151B2B] border border-[#2A3441] rounded-xl p-6" data-testid="affiliate-assignment">
        <h3 className="text-white font-semibold flex items-center gap-2 mb-4">
          <UserCheck className="h-4 w-4 text-[#0099CC]" />
          Affilie
        </h3>
        {currentAffiliate && (
          <div className="mb-3 flex items-center gap-2">
            <Badge className="bg-[#10B981]/20 text-[#10B981] border-0">
              {currentAffiliate.name} ({currentAffiliate.commission_rate}%)
            </Badge>
          </div>
        )}
        <div className="flex items-end gap-3">
          <div className="flex-1 space-y-1">
            <Label className="text-[#94A3B8] text-xs">Attribuer un affilie</Label>
            <select data-testid="affiliate-select" value={selectedAffiliate}
              onChange={(e) => setSelectedAffiliate(e.target.value)}
              className="w-full bg-[#0B0F19] border border-[#2A3441] text-white rounded-md px-3 py-2 h-10">
              <option value="">-- Aucun affilie --</option>
              {affiliates.map(a => (
                <option key={a.id} value={a.id}>{a.name} ({a.commission_rate}%)</option>
              ))}
            </select>
          </div>
          <Button onClick={assignAffiliate} disabled={savingAffiliate}
            className="bg-[#0099CC] hover:bg-[#0088B5] text-white" data-testid="assign-affiliate-btn">
            {savingAffiliate ? <Loader2 className="h-4 w-4 animate-spin" /> : "Attribuer"}
          </Button>
        </div>
      </div>

      {/* Client Account Access */}
      <ClientAccountSection client={client} onClientUpdate={onClientUpdate} />

      <div className="flex gap-3">
        <Link to={`/app/projets?client_id=${client.id}`}>
          <Button variant="outline" className="border-[#2A3441] text-[#94A3B8] hover:text-white" data-testid="view-projects-btn">
            <FolderKanban className="h-4 w-4 mr-2" />
            Voir les projets
          </Button>
        </Link>
        <Button variant="outline"
          onClick={async () => {
            if (!window.confirm(`Supprimer le client "${client.company_name}" ? Cette action est irreversible.`)) return;
            try {
              await axios.delete(`${API}/clients/${client.id}`);
              toast.success("Client supprime");
              window.location.href = "/app/clients";
            } catch (e) {
              toast.error(e.response?.data?.detail || "Erreur de suppression");
            }
          }}
          className="border-[#EF4444]/30 text-[#EF4444] hover:bg-[#EF4444]/10"
          data-testid="delete-client-btn">
          <Trash2 className="h-4 w-4 mr-2" />
          Supprimer le client
        </Button>
      </div>
    </div>
  );
}

function ClientPricingTab({ client }) {
  const [plans, setPlans] = useState([]);
  const [pricing, setPricing] = useState(null);
  const [effective, setEffective] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [checkoutUrl, setCheckoutUrl] = useState(null);
  const [revalModal, setRevalModal] = useState(false);
  const [revalForm, setRevalForm] = useState({ new_monthly_fee: "", reason: "" });
  const [revalSaving, setRevalSaving] = useState(false);
  const [revalHistory, setRevalHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);

  const [form, setForm] = useState({
    plan_id: "",
    discount_type: "percent",
    discount_value: 0,
    valid_from: new Date().toISOString().split("T")[0],
    valid_to: "",
    notes: "",
  });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [plansRes, pricingRes, subRes] = await Promise.all([
        axios.get(`${API}/billing/plans`),
        axios.get(`${API}/billing/clients/${client.id}/pricing`),
        axios.get(`${API}/billing/clients/${client.id}/subscription`).catch(() => ({ data: null })),
      ]);
      const activePlans = (plansRes.data || []).filter((p) => p.status === "active");
      setPlans(activePlans);
      setSubscription(subRes.data);

      if (pricingRes.data) {
        setPricing(pricingRes.data);
        setForm({
          plan_id: pricingRes.data.plan_id || "",
          discount_type: pricingRes.data.discount_type || "percent",
          discount_value: pricingRes.data.discount_value || 0,
          valid_from: pricingRes.data.valid_from || new Date().toISOString().split("T")[0],
          valid_to: pricingRes.data.valid_to || "",
          notes: pricingRes.data.notes || "",
        });

        try {
          const effRes = await axios.get(`${API}/billing/clients/${client.id}/effective-pricing`);
          setEffective(effRes.data);
        } catch {
          setEffective(null);
        }
      }
    } catch {
      toast.error("Erreur chargement tarification");
    } finally {
      setLoading(false);
    }
  }, [client.id]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.plan_id) {
      toast.error("Selectionnez un plan");
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/billing/clients/${client.id}/pricing`, {
        plan_id: form.plan_id,
        discount_type: form.discount_type,
        discount_value: Number(form.discount_value),
        valid_from: form.valid_from || null,
        valid_to: form.valid_to || null,
        notes: form.notes || null,
      });
      toast.success("Tarification client mise a jour");
      setCheckoutUrl(null);
      await fetchData();
    } catch {
      toast.error("Erreur mise a jour tarification");
    } finally {
      setSaving(false);
    }
  };

  const generateCheckout = async () => {
    setCheckoutLoading(true);
    try {
      const originUrl = window.location.origin;
      const res = await axios.post(`${API}/billing/clients/${client.id}/checkout`, {
        origin_url: originUrl,
      });
      setCheckoutUrl(res.data.checkout_url);
      toast.success("Lien de paiement genere !");
    } catch (err) {
      const msg = err.response?.data?.detail || "Erreur generation checkout";
      toast.error(msg);
    } finally {
      setCheckoutLoading(false);
    }
  };

  const copyCheckoutUrl = () => {
    if (checkoutUrl) {
      navigator.clipboard.writeText(checkoutUrl);
      toast.success("Lien copie dans le presse-papiers");
    }
  };

  const openRevalModal = () => {
    setRevalForm({
      new_monthly_fee: effective?.final_monthly_fee || "",
      reason: "Revalorisation annuelle",
    });
    setRevalModal(true);
  };

  const handleRevalue = async () => {
    const fee = parseFloat(revalForm.new_monthly_fee);
    if (!fee || fee <= 0) { toast.error("Montant invalide"); return; }
    setRevalSaving(true);
    try {
      await axios.post(`${API}/subscriptions/${client.id}/revalue`, {
        new_monthly_fee: fee,
        reason: revalForm.reason || null,
      });
      toast.success("Revalorisation appliquee avec succes");
      setRevalModal(false);
      await fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur revalorisation");
    } finally {
      setRevalSaving(false);
    }
  };

  const fetchRevalHistory = async () => {
    try {
      const res = await axios.get(`${API}/subscriptions/${client.id}/revalorization-history`);
      setRevalHistory(res.data || []);
      setShowHistory(true);
    } catch { toast.error("Erreur chargement historique"); }
  };

  const selectedPlan = plans.find((p) => p.id === form.plan_id);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-8 w-8 animate-spin text-[#0099CC]" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="client-pricing-tab">
      {/* Subscription Status */}
      {subscription && (
        <div className={`border rounded-xl p-4 flex items-center gap-3 ${
          subscription.status === "active"
            ? "border-[#10B981]/30 bg-[#10B981]/5"
            : "border-[#F59E0B]/30 bg-[#F59E0B]/5"
        }`} data-testid="subscription-status">
          {subscription.status === "active" ? (
            <CheckCircle2 className="h-5 w-5 text-[#10B981]" />
          ) : (
            <Clock className="h-5 w-5 text-[#F59E0B]" />
          )}
          <div>
            <p className="text-white font-medium">
              Abonnement {subscription.status === "active" ? "actif" : subscription.status}
            </p>
            {subscription.started_at && (
              <p className="text-xs text-[#94A3B8]">
                Depuis le {new Date(subscription.started_at).toLocaleDateString("fr-FR")}
              </p>
            )}
          </div>
        </div>
      )}

      {/* Effective Pricing Summary */}
      {effective && (
        <div className="bg-[#0099CC]/5 border border-[#0099CC]/20 rounded-xl p-6" data-testid="effective-pricing-summary">
          <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
            <Euro className="h-4 w-4 text-[#0099CC]" />
            Tarification effective
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <p className="text-xs text-[#64748b] uppercase tracking-wider">Plan</p>
              <p className="text-white font-semibold mt-1">{effective.plan_name}</p>
            </div>
            <div>
              <p className="text-xs text-[#64748b] uppercase tracking-wider">Mensuel</p>
              <p className="text-2xl font-bold text-[#0099CC] mt-1">
                {effective.final_monthly_fee.toLocaleString("fr-FR")} <span className="text-sm font-normal">EUR</span>
              </p>
              {effective.discount_value > 0 && (
                <p className="text-xs text-[#64748b] line-through">{effective.base_monthly_fee.toLocaleString("fr-FR")} EUR</p>
              )}
            </div>
            <div>
              <p className="text-xs text-[#64748b] uppercase tracking-wider">Setup</p>
              <p className="text-lg font-semibold text-white mt-1">
                {effective.final_setup_fee.toLocaleString("fr-FR")} <span className="text-sm font-normal text-[#94A3B8]">EUR</span>
              </p>
              {effective.discount_value > 0 && (
                <p className="text-xs text-[#64748b] line-through">{effective.base_setup_fee.toLocaleString("fr-FR")} EUR</p>
              )}
            </div>
            <div>
              <p className="text-xs text-[#64748b] uppercase tracking-wider">Remise</p>
              <p className="text-lg font-semibold text-[#10B981] mt-1">
                {effective.discount_value > 0
                  ? effective.discount_type === "percent"
                    ? `-${effective.discount_value}%`
                    : effective.discount_type === "fixed"
                    ? `-${effective.discount_value.toLocaleString("fr-FR")} EUR`
                    : "Prix fixe"
                  : "Aucune"}
              </p>
            </div>
          </div>

          {/* Checkout Button */}
          <div className="mt-5 pt-4 border-t border-[#0099CC]/20">
            <div className="flex flex-wrap items-center gap-3">
              <Button
                onClick={generateCheckout}
                disabled={checkoutLoading}
                className="bg-[#0099CC] hover:bg-[#0088B5] text-white"
                data-testid="generate-checkout-btn"
              >
                {checkoutLoading ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <CreditCard className="h-4 w-4 mr-2" />
                )}
                Generer lien de paiement
              </Button>
              <p className="text-xs text-[#64748b]">
                Total: {(effective.final_monthly_fee + effective.final_setup_fee).toLocaleString("fr-FR")} EUR
                (mensuel + setup)
              </p>
            </div>

            {checkoutUrl && (
              <div className="mt-3 bg-[#0B0F19] border border-[#2A3441] rounded-lg p-4" data-testid="checkout-url-display">
                <p className="text-xs text-[#94A3B8] mb-2">Lien de paiement Stripe :</p>
                <div className="flex items-center gap-2">
                  <input
                    readOnly
                    value={checkoutUrl}
                    className="flex-1 bg-transparent text-[#0099CC] text-sm truncate outline-none"
                    data-testid="checkout-url-input"
                  />
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={copyCheckoutUrl}
                    className="border-[#2A3441] text-[#94A3B8] hover:text-white shrink-0"
                    data-testid="copy-checkout-url-btn"
                  >
                    <Copy className="h-3.5 w-3.5 mr-1" />
                    Copier
                  </Button>
                  <a href={checkoutUrl} target="_blank" rel="noopener noreferrer">
                    <Button
                      variant="outline"
                      size="sm"
                      className="border-[#2A3441] text-[#94A3B8] hover:text-white shrink-0"
                      data-testid="open-checkout-btn"
                    >
                      <ExternalLink className="h-3.5 w-3.5 mr-1" />
                      Ouvrir
                    </Button>
                  </a>
                </div>
              </div>
            )}
          </div>

          <p className="text-xs text-[#64748b] mt-3">TVA non applicable, art. 293 B du CGI</p>

          {/* Revalorisation Button */}
          <div className="mt-4 pt-4 border-t border-[#0099CC]/20 flex flex-wrap items-center gap-3">
            <Button
              onClick={openRevalModal}
              variant="outline"
              className="border-[#F59E0B]/30 text-[#F59E0B] hover:bg-[#F59E0B]/10 hover:text-[#F59E0B]"
              data-testid="revalue-btn"
            >
              <TrendingUp className="h-4 w-4 mr-2" />
              Revaloriser
            </Button>
            <Button
              onClick={fetchRevalHistory}
              variant="ghost"
              size="sm"
              className="text-[#64748b] hover:text-[#94A3B8]"
              data-testid="reval-history-btn"
            >
              <History className="h-4 w-4 mr-1" />
              Historique
            </Button>
          </div>
        </div>
      )}

      {/* Pricing Form */}
      <div className="bg-[#151B2B] border border-[#2A3441] rounded-xl p-6">
        <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
          <Tag className="h-4 w-4 text-[#0099CC]" />
          {pricing ? "Modifier la tarification" : "Attribuer un plan"}
        </h3>

        {plans.length === 0 ? (
          <div className="text-center py-8">
            <CreditCard className="h-10 w-10 text-[#64748b] mx-auto mb-3" />
            <p className="text-[#94A3B8]">Aucun plan actif disponible.</p>
            <Link to="/app/plans">
              <Button variant="link" className="text-[#0099CC] mt-2">Creer un plan</Button>
            </Link>
          </div>
        ) : (
          <form onSubmit={handleSave} className="space-y-5" data-testid="client-pricing-form">
            <div className="space-y-2">
              <Label className="text-[#94A3B8]">Plan tarifaire</Label>
              <select
                data-testid="pricing-plan-select"
                value={form.plan_id}
                onChange={(e) => setForm({ ...form, plan_id: e.target.value })}
                className="w-full bg-[#0B0F19] border border-[#2A3441] text-white rounded-md px-3 py-2 h-10"
              >
                <option value="">-- Selectionnez un plan --</option>
                {plans.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name} ({p.monthly_fee.toLocaleString("fr-FR")} EUR/mois)
                  </option>
                ))}
              </select>
            </div>

            {selectedPlan && (
              <div className="bg-[#0B0F19] border border-[#2A3441] rounded-lg p-4" data-testid="selected-plan-preview">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-white font-medium">{selectedPlan.name}</p>
                    <p className="text-xs text-[#64748b]">{selectedPlan.description}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-white font-bold">{selectedPlan.monthly_fee.toLocaleString("fr-FR")} EUR</p>
                    <p className="text-xs text-[#64748b]">Setup: {selectedPlan.setup_fee.toLocaleString("fr-FR")} EUR</p>
                  </div>
                </div>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-[#94A3B8] flex items-center gap-1">
                  <Percent className="h-3.5 w-3.5" />
                  Type de remise
                </Label>
                <select
                  data-testid="discount-type-select"
                  value={form.discount_type}
                  onChange={(e) => setForm({ ...form, discount_type: e.target.value })}
                  className="w-full bg-[#0B0F19] border border-[#2A3441] text-white rounded-md px-3 py-2 h-10"
                >
                  <option value="percent">Pourcentage (%)</option>
                  <option value="fixed">Montant fixe (EUR)</option>
                  <option value="override">Prix impose (override)</option>
                </select>
              </div>
              <div className="space-y-2">
                <Label className="text-[#94A3B8]">
                  {form.discount_type === "percent"
                    ? "Remise (%)"
                    : form.discount_type === "fixed"
                    ? "Remise (EUR)"
                    : "Prix final (EUR)"}
                </Label>
                <Input
                  data-testid="discount-value-input"
                  type="number"
                  step="0.01"
                  min="0"
                  value={form.discount_value}
                  onChange={(e) => setForm({ ...form, discount_value: Number(e.target.value) })}
                  className="bg-[#0B0F19] border-[#2A3441] text-white"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-[#94A3B8] flex items-center gap-1">
                  <Calendar className="h-3.5 w-3.5" />
                  Debut de validite
                </Label>
                <Input
                  data-testid="valid-from-input"
                  type="date"
                  value={form.valid_from}
                  onChange={(e) => setForm({ ...form, valid_from: e.target.value })}
                  className="bg-[#0B0F19] border-[#2A3441] text-white"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-[#94A3B8] flex items-center gap-1">
                  <Calendar className="h-3.5 w-3.5" />
                  Fin de validite (optionnel)
                </Label>
                <Input
                  data-testid="valid-to-input"
                  type="date"
                  value={form.valid_to}
                  onChange={(e) => setForm({ ...form, valid_to: e.target.value })}
                  className="bg-[#0B0F19] border-[#2A3441] text-white"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-[#94A3B8] flex items-center gap-1">
                <FileText className="h-3.5 w-3.5" />
                Notes internes
              </Label>
              <Input
                data-testid="pricing-notes-input"
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                placeholder="Ex: Remise commerciale premier contrat..."
                className="bg-[#0B0F19] border-[#2A3441] text-white"
              />
            </div>

            <div className="flex gap-3 pt-2">
              <Button
                type="submit"
                disabled={saving}
                className="bg-[#0099CC] hover:bg-[#0088B5] text-white"
                data-testid="save-pricing-btn"
              >
                {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                {pricing ? "Mettre a jour" : "Attribuer le plan"}
              </Button>
            </div>
          </form>
        )}
      </div>

      {/* Revalorisation Modal */}
      {revalModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm" data-testid="reval-modal">
          <div className="bg-[#151B2B] rounded-xl border border-[#2A3441] p-6 w-full max-w-md mx-4">
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-white font-semibold flex items-center gap-2">
                <TrendingUp className="h-5 w-5 text-[#F59E0B]" />
                Revalorisation abonnement
              </h3>
              <button onClick={() => setRevalModal(false)} className="text-[#64748b] hover:text-white">
                <X className="h-5 w-5" />
              </button>
            </div>

            {effective && (
              <div className="bg-[#0B0F19] border border-[#2A3441] rounded-lg p-3 mb-4">
                <p className="text-xs text-[#64748b]">Tarif mensuel actuel</p>
                <p className="text-xl font-bold text-white">{effective.final_monthly_fee.toLocaleString("fr-FR")} EUR</p>
              </div>
            )}

            <div className="space-y-4">
              <div>
                <label className="text-[#94A3B8] text-sm mb-1 block">Nouveau tarif mensuel (EUR)</label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={revalForm.new_monthly_fee}
                  onChange={e => setRevalForm(f => ({ ...f, new_monthly_fee: e.target.value }))}
                  placeholder="Ex: 250.00"
                  className="bg-[#0B0F19] border-[#2A3441] text-white"
                  data-testid="reval-new-fee-input"
                />
              </div>
              <div>
                <label className="text-[#94A3B8] text-sm mb-1 block">Motif</label>
                <Input
                  value={revalForm.reason}
                  onChange={e => setRevalForm(f => ({ ...f, reason: e.target.value }))}
                  placeholder="Revalorisation annuelle"
                  className="bg-[#0B0F19] border-[#2A3441] text-white"
                  data-testid="reval-reason-input"
                />
              </div>

              {revalForm.new_monthly_fee && effective && (
                <div className="bg-[#0B0F19] border border-[#2A3441] rounded-lg p-3">
                  <div className="flex justify-between text-sm">
                    <span className="text-[#64748b]">Difference</span>
                    <span className={`font-semibold ${
                      parseFloat(revalForm.new_monthly_fee) > effective.final_monthly_fee ? "text-[#F59E0B]" : "text-[#10B981]"
                    }`}>
                      {parseFloat(revalForm.new_monthly_fee) > effective.final_monthly_fee ? "+" : ""}
                      {(parseFloat(revalForm.new_monthly_fee) - effective.final_monthly_fee).toLocaleString("fr-FR", { minimumFractionDigits: 2 })} EUR
                    </span>
                  </div>
                </div>
              )}
            </div>

            <div className="flex gap-3 mt-6">
              <Button onClick={() => setRevalModal(false)} variant="outline" className="flex-1 border-[#2A3441] text-[#94A3B8]">
                Annuler
              </Button>
              <Button
                onClick={handleRevalue}
                disabled={revalSaving}
                className="flex-1 bg-[#F59E0B] hover:bg-[#D97706] text-black font-semibold"
                data-testid="reval-confirm-btn"
              >
                {revalSaving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Appliquer
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Revalorisation History Modal */}
      {showHistory && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm" data-testid="reval-history-modal">
          <div className="bg-[#151B2B] rounded-xl border border-[#2A3441] p-6 w-full max-w-lg mx-4 max-h-[80vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-white font-semibold flex items-center gap-2">
                <History className="h-5 w-5 text-[#0099CC]" />
                Historique revalorisations
              </h3>
              <button onClick={() => setShowHistory(false)} className="text-[#64748b] hover:text-white">
                <X className="h-5 w-5" />
              </button>
            </div>

            {revalHistory.length === 0 ? (
              <p className="text-[#64748b] text-center py-8">Aucune revalorisation enregistree.</p>
            ) : (
              <div className="space-y-3">
                {revalHistory.map(log => (
                  <div key={log.id} className="bg-[#0B0F19] border border-[#2A3441] rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs text-[#64748b]">
                        {new Date(log.created_at).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" })}
                      </span>
                      <span className={`text-sm font-bold ${log.new_monthly_fee > log.old_monthly_fee ? "text-[#F59E0B]" : "text-[#10B981]"}`}>
                        {log.old_monthly_fee.toLocaleString("fr-FR")} → {log.new_monthly_fee.toLocaleString("fr-FR")} EUR
                      </span>
                    </div>
                    <p className="text-[#94A3B8] text-sm">{log.reason}</p>
                  </div>
                ))}
              </div>
            )}

            <Button onClick={() => setShowHistory(false)} variant="outline" className="w-full mt-4 border-[#2A3441] text-[#94A3B8]">
              Fermer
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

function SaveCardForm({ clientId, onSuccess, onCancel }) {
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const [intentData, setIntentData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    axios.post(`${API}/admin/clients/${clientId}/setup-intent`)
      .then(res => setIntentData(res.data))
      .catch(err => setError(err.response?.data?.detail || "Erreur SetupIntent"));
  }, [clientId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements || !intentData) return;
    setLoading(true);
    setError(null);

    const { error: stripeError, setupIntent } = await stripe.confirmCardSetup(
      intentData.client_secret,
      { payment_method: { card: elements.getElement(CardElement) } }
    );

    if (stripeError) {
      setError(stripeError.message);
      setLoading(false);
      return;
    }

    if (setupIntent.status === "succeeded") {
      toast.success("Carte enregistree avec succes !");
      onSuccess();
    } else {
      setError(`Statut: ${setupIntent.status}`);
    }
    setLoading(false);
  };

  if (!intentData && !error) {
    return <div className="flex justify-center py-6"><Loader2 className="h-5 w-5 animate-spin text-[#0099CC]" /></div>;
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4" data-testid="save-card-form">
      <div>
        <label className="text-[#94A3B8] text-sm mb-2 block">Carte bancaire du client</label>
        <div className="bg-[#0B0F19] border border-[#2A3441] rounded-lg p-4">
          <CardElement options={{
            style: {
              base: { color: "#E2E8F0", fontFamily: "'Segoe UI', sans-serif", fontSize: "16px", "::placeholder": { color: "#64748b" } },
              invalid: { color: "#EF4444" },
            },
          }} />
        </div>
      </div>
      {error && <p className="text-[#EF4444] text-sm">{error}</p>}
      <div className="flex gap-3">
        <Button type="button" onClick={onCancel} variant="outline" className="flex-1 border-[#2A3441] text-[#94A3B8]">Annuler</Button>
        <Button type="submit" disabled={loading || !stripe} className="flex-1 bg-[#0099CC] hover:bg-[#0088B5] text-white" data-testid="confirm-save-card-btn">
          {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <CreditCard className="h-4 w-4 mr-2" />}
          Enregistrer la carte
        </Button>
      </div>
    </form>
  );
}

function SaveSepaForm({ clientId, clientEmail, onSuccess, onCancel }) {
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const [intentData, setIntentData] = useState(null);
  const [error, setError] = useState(null);
  const [accountHolder, setAccountHolder] = useState("");
  const [email, setEmail] = useState(clientEmail || "");

  useEffect(() => {
    axios.post(`${API}/admin/clients/${clientId}/setup-intent-sepa`)
      .then(res => setIntentData(res.data))
      .catch(err => setError(err.response?.data?.detail || "Erreur SetupIntent SEPA"));
  }, [clientId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements || !intentData || !accountHolder.trim()) return;
    setLoading(true);
    setError(null);

    const ibanElement = elements.getElement(IbanElement);
    const { error: stripeError, setupIntent } = await stripe.confirmSepaDebitSetup(
      intentData.client_secret,
      {
        payment_method: {
          sepa_debit: ibanElement,
          billing_details: { name: accountHolder, email: email },
        },
      }
    );

    if (stripeError) {
      setError(stripeError.message);
      setLoading(false);
      return;
    }

    if (setupIntent.status === "succeeded" || setupIntent.status === "processing") {
      toast.success("RIB/IBAN enregistre avec succes !");
      onSuccess();
    } else {
      setError(`Statut: ${setupIntent.status}`);
    }
    setLoading(false);
  };

  if (!intentData && !error) {
    return <div className="flex justify-center py-6"><Loader2 className="h-5 w-5 animate-spin text-[#0099CC]" /></div>;
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4" data-testid="save-sepa-form">
      <div>
        <label className="text-[#94A3B8] text-sm mb-2 block">Titulaire du compte</label>
        <Input
          value={accountHolder}
          onChange={(e) => setAccountHolder(e.target.value)}
          placeholder="Nom du titulaire"
          className="bg-[#0B0F19] border-[#2A3441] text-white"
          data-testid="sepa-holder-input"
        />
      </div>
      <div>
        <label className="text-[#94A3B8] text-sm mb-2 block">Email du titulaire</label>
        <Input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="email@example.com"
          className="bg-[#0B0F19] border-[#2A3441] text-white"
          data-testid="sepa-email-input"
        />
      </div>
      <div>
        <label className="text-[#94A3B8] text-sm mb-2 block">IBAN</label>
        <div className="bg-[#0B0F19] border border-[#2A3441] rounded-lg p-4">
          <IbanElement options={{
            supportedCountries: ["SEPA"],
            style: {
              base: { color: "#E2E8F0", fontFamily: "'Segoe UI', sans-serif", fontSize: "16px", "::placeholder": { color: "#64748b" } },
              invalid: { color: "#EF4444" },
            },
          }} />
        </div>
      </div>
      <p className="text-[#64748b] text-xs">En fournissant votre IBAN, vous autorisez GLOTECH DIGITAL a envoyer des instructions de prelevement SEPA.</p>
      {error && <p className="text-[#EF4444] text-sm">{error}</p>}
      <div className="flex gap-3">
        <Button type="button" onClick={onCancel} variant="outline" className="flex-1 border-[#2A3441] text-[#94A3B8]">Annuler</Button>
        <Button type="submit" disabled={loading || !stripe || !accountHolder.trim() || !email.trim()} className="flex-1 bg-[#10B981] hover:bg-[#059669] text-white" data-testid="confirm-save-sepa-btn">
          {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Landmark className="h-4 w-4 mr-2" />}
          Enregistrer le RIB
        </Button>
      </div>
    </form>
  );
}

function ClientPaymentsTab({ client }) {
  const [payments, setPayments] = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showSaveCard, setShowSaveCard] = useState(false);
  const [showSaveSepa, setShowSaveSepa] = useState(false);
  const [showNewSub, setShowNewSub] = useState(false);
  const [subForm, setSubForm] = useState({ amount: "", interval: "month", description: "Abonnement mensuel" });
  const [creatingSub, setCreatingSub] = useState(false);
  const [actionLoading, setActionLoading] = useState(null);
  const [stripePromise, setStripePromise] = useState(null);
  const [deletingPm, setDeletingPm] = useState(null);

  useEffect(() => {
    const pk = process.env.REACT_APP_STRIPE_PUBLIC_KEY;
    if (pk) setStripePromise(loadStripe(pk));
  }, []);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [payRes, subRes, pmRes] = await Promise.all([
        axios.get(`${API}/billing/clients/${client.id}/payments`),
        axios.get(`${API}/admin/clients/${client.id}/stripe-subscriptions`),
        axios.get(`${API}/admin/clients/${client.id}/payment-methods`),
      ]);
      setPayments(payRes.data || []);
      setSubscriptions(subRes.data || []);
      setPaymentMethods(pmRes.data || []);
    } catch {
      toast.error("Erreur chargement paiements");
    } finally {
      setLoading(false);
    }
  }, [client.id]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleCreateSub = async (e) => {
    e.preventDefault();
    const amount = parseFloat(subForm.amount);
    if (!amount || amount <= 0) { toast.error("Montant invalide"); return; }
    setCreatingSub(true);
    try {
      await axios.post(`${API}/admin/clients/${client.id}/stripe-subscription`, {
        amount,
        interval: subForm.interval,
        description: subForm.description,
      });
      toast.success("Abonnement cree avec succes !");
      setShowNewSub(false);
      setSubForm({ amount: "", interval: "month", description: "Abonnement mensuel" });
      await fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur creation abonnement");
    } finally {
      setCreatingSub(false);
    }
  };

  const handleSubAction = async (subId, action) => {
    setActionLoading(`${subId}-${action}`);
    try {
      await axios.post(`${API}/admin/clients/${client.id}/stripe-subscriptions/${subId}/${action}`);
      toast.success(action === "cancel" ? "Abonnement annule" : "Abonnement mis a jour");
      await fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    } finally {
      setActionLoading(null);
    }
  };

  const handleDeletePm = async (pmId) => {
    setDeletingPm(pmId);
    try {
      await axios.delete(`${API}/admin/clients/${client.id}/payment-methods/${pmId}`);
      toast.success("Moyen de paiement supprime");
      await fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur suppression");
    } finally {
      setDeletingPm(null);
    }
  };

  const paymentStatusConfig = {
    paid: { label: "Paye", icon: CheckCircle2, color: "text-[#10B981]", bg: "bg-[#10B981]/10" },
    initiated: { label: "En attente", icon: Clock, color: "text-[#F59E0B]", bg: "bg-[#F59E0B]/10" },
    failed: { label: "Echoue", icon: XCircle, color: "text-[#EF4444]", bg: "bg-[#EF4444]/10" },
    expired: { label: "Expire", icon: XCircle, color: "text-[#64748b]", bg: "bg-[#64748b]/10" },
  };

  const subStatusConfig = {
    active: { label: "Actif", color: "text-[#10B981]", bg: "bg-[#10B981]/10" },
    paused: { label: "Suspendu", color: "text-[#F59E0B]", bg: "bg-[#F59E0B]/10" },
    canceled: { label: "Annule", color: "text-[#EF4444]", bg: "bg-[#EF4444]/10" },
    past_due: { label: "En retard", color: "text-[#EF4444]", bg: "bg-[#EF4444]/10" },
    incomplete: { label: "Incomplet", color: "text-[#64748b]", bg: "bg-[#64748b]/10" },
  };

  if (loading) {
    return <div className="flex items-center justify-center py-16"><Loader2 className="h-8 w-8 animate-spin text-[#0099CC]" /></div>;
  }

  return (
    <div className="space-y-6" data-testid="client-payments-tab">
      {/* Payment Methods */}
      <div className="bg-[#151B2B] border border-[#2A3441] rounded-xl p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-white font-semibold flex items-center gap-2">
            <CreditCard className="h-4 w-4 text-[#0099CC]" />
            Moyens de paiement
          </h3>
          <div className="flex gap-2">
            <Button
              onClick={() => { setShowSaveCard(true); setShowSaveSepa(false); }}
              size="sm"
              className="bg-[#0099CC] hover:bg-[#0088B5] text-white"
              data-testid="add-card-btn"
            >
              <CreditCard className="h-3.5 w-3.5 mr-1" />
              Carte
            </Button>
            <Button
              onClick={() => { setShowSaveSepa(true); setShowSaveCard(false); }}
              size="sm"
              className="bg-[#10B981] hover:bg-[#059669] text-white"
              data-testid="add-sepa-btn"
            >
              <Landmark className="h-3.5 w-3.5 mr-1" />
              RIB / IBAN
            </Button>
          </div>
        </div>

        {paymentMethods.length === 0 ? (
          <p className="text-[#64748b] text-sm text-center py-4">Aucun moyen de paiement enregistre.</p>
        ) : (
          <div className="space-y-2">
            {paymentMethods.map((pm) => (
              <div key={pm.id} className="bg-[#0B0F19] border border-[#2A3441] rounded-lg p-3 flex items-center gap-3" data-testid={`pm-${pm.id}`}>
                {pm.type === "sepa_debit" ? (
                  <Landmark className="h-5 w-5 text-[#10B981]" />
                ) : (
                  <CreditCard className="h-5 w-5 text-[#0099CC]" />
                )}
                <div className="flex-1">
                  {pm.type === "sepa_debit" ? (
                    <>
                      <span className="text-white font-medium">SEPA</span>
                      <span className="text-[#94A3B8] ml-2">**** {pm.last4}</span>
                      {pm.country && <span className="text-[#64748b] ml-2 text-xs">{pm.country}</span>}
                    </>
                  ) : (
                    <>
                      <span className="text-white font-medium capitalize">{pm.brand}</span>
                      <span className="text-[#94A3B8] ml-2">**** {pm.last4}</span>
                    </>
                  )}
                </div>
                {pm.type === "card" && <span className="text-[#64748b] text-sm">{pm.exp_month}/{pm.exp_year}</span>}
                <button
                  onClick={() => handleDeletePm(pm.id)}
                  disabled={deletingPm === pm.id}
                  className="text-[#EF4444]/60 hover:text-[#EF4444] transition-colors p-1"
                  data-testid={`delete-pm-${pm.id}`}
                >
                  {deletingPm === pm.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <X className="h-4 w-4" />}
                </button>
              </div>
            ))}
          </div>
        )}

        {showSaveCard && stripePromise && (
          <div className="mt-4 pt-4 border-t border-[#2A3441]">
            <Elements stripe={stripePromise}>
              <SaveCardForm
                clientId={client.id}
                onSuccess={() => { setShowSaveCard(false); fetchAll(); }}
                onCancel={() => setShowSaveCard(false)}
              />
            </Elements>
          </div>
        )}

        {showSaveSepa && stripePromise && (
          <div className="mt-4 pt-4 border-t border-[#2A3441]">
            <Elements stripe={stripePromise}>
              <SaveSepaForm
                clientId={client.id}
                clientEmail={client.contact_email || ""}
                onSuccess={() => { setShowSaveSepa(false); fetchAll(); }}
                onCancel={() => setShowSaveSepa(false)}
              />
            </Elements>
          </div>
        )}
      </div>

      {/* Recurring Subscriptions */}
      <div className="bg-[#151B2B] border border-[#2A3441] rounded-xl p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-white font-semibold flex items-center gap-2">
            <RefreshCw className="h-4 w-4 text-[#0099CC]" />
            Abonnements recurrents
          </h3>
          <Button
            onClick={() => setShowNewSub(true)}
            size="sm"
            className="bg-[#10B981] hover:bg-[#0D9668] text-white"
            data-testid="new-subscription-btn"
            disabled={paymentMethods.length === 0}
          >
            <RefreshCw className="h-3.5 w-3.5 mr-1" />
            Nouvel abonnement
          </Button>
        </div>

        {paymentMethods.length === 0 && (
          <p className="text-[#F59E0B] text-sm mb-4">Enregistrez d'abord une carte pour creer un abonnement.</p>
        )}

        {subscriptions.length === 0 ? (
          <p className="text-[#64748b] text-sm text-center py-4">Aucun abonnement actif.</p>
        ) : (
          <div className="space-y-3">
            {subscriptions.map((sub) => {
              const cfg = subStatusConfig[sub.status] || subStatusConfig.incomplete;
              return (
                <div key={sub.id} className="bg-[#0B0F19] border border-[#2A3441] rounded-xl p-4" data-testid={`sub-${sub.id}`}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <p className="text-white font-medium">{sub.description || "Abonnement"}</p>
                      <Badge className={`${cfg.bg} ${cfg.color} border-0 text-xs`}>{cfg.label}</Badge>
                    </div>
                    <p className="text-[#0099CC] font-bold">
                      {sub.amount?.toLocaleString("fr-FR")} EUR/{sub.interval === "year" ? "an" : "mois"}
                    </p>
                  </div>
                  {sub.current_period_end && (
                    <p className="text-xs text-[#64748b] mb-3">
                      Prochaine echeance : {new Date(sub.current_period_end).toLocaleDateString("fr-FR")}
                    </p>
                  )}
                  {sub.status !== "canceled" && (
                    <div className="flex gap-2">
                      <Button
                        onClick={() => handleSubAction(sub.id, "pause")}
                        disabled={actionLoading === `${sub.id}-pause`}
                        size="sm"
                        variant="outline"
                        className="border-[#F59E0B]/30 text-[#F59E0B] hover:bg-[#F59E0B]/10 text-xs"
                        data-testid={`pause-sub-${sub.id}`}
                      >
                        {actionLoading === `${sub.id}-pause` ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> :
                          sub.status === "paused" ? <Play className="h-3 w-3 mr-1" /> : <Pause className="h-3 w-3 mr-1" />}
                        {sub.status === "paused" ? "Reprendre" : "Suspendre"}
                      </Button>
                      <Button
                        onClick={() => handleSubAction(sub.id, "cancel")}
                        disabled={actionLoading === `${sub.id}-cancel`}
                        size="sm"
                        variant="outline"
                        className="border-[#EF4444]/30 text-[#EF4444] hover:bg-[#EF4444]/10 text-xs"
                        data-testid={`cancel-sub-${sub.id}`}
                      >
                        {actionLoading === `${sub.id}-cancel` ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Ban className="h-3 w-3 mr-1" />}
                        Annuler
                      </Button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* New Subscription Form */}
        {showNewSub && (
          <form onSubmit={handleCreateSub} className="mt-4 pt-4 border-t border-[#2A3441] space-y-4" data-testid="new-sub-form">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-1.5">
                <Label className="text-[#94A3B8] text-xs">Montant (EUR)</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="1"
                  value={subForm.amount}
                  onChange={(e) => setSubForm({ ...subForm, amount: e.target.value })}
                  placeholder="149.00"
                  className="bg-[#0B0F19] border-[#2A3441] text-white"
                  data-testid="sub-amount-input"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-[#94A3B8] text-xs">Frequence</Label>
                <select
                  value={subForm.interval}
                  onChange={(e) => setSubForm({ ...subForm, interval: e.target.value })}
                  className="w-full bg-[#0B0F19] border border-[#2A3441] text-white rounded-md px-3 py-2 h-10"
                  data-testid="sub-interval-select"
                >
                  <option value="month">Mensuel</option>
                  <option value="year">Annuel</option>
                </select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-[#94A3B8] text-xs">Description</Label>
                <Input
                  value={subForm.description}
                  onChange={(e) => setSubForm({ ...subForm, description: e.target.value })}
                  placeholder="Abonnement mensuel"
                  className="bg-[#0B0F19] border-[#2A3441] text-white"
                  data-testid="sub-description-input"
                />
              </div>
            </div>
            <div className="flex gap-3">
              <Button type="button" onClick={() => setShowNewSub(false)} variant="outline" className="border-[#2A3441] text-[#94A3B8]">Annuler</Button>
              <Button type="submit" disabled={creatingSub} className="bg-[#10B981] hover:bg-[#0D9668] text-white" data-testid="create-sub-btn">
                {creatingSub ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}
                Creer l'abonnement
              </Button>
            </div>
          </form>
        )}
      </div>

      {/* Payment History */}
      <div className="bg-[#151B2B] border border-[#2A3441] rounded-xl p-6">
        <h3 className="text-white font-semibold flex items-center gap-2 mb-4">
          <Receipt className="h-4 w-4 text-[#0099CC]" />
          Historique des paiements
        </h3>

        {payments.length === 0 ? (
          <div className="text-center py-8">
            <Receipt className="h-10 w-10 text-[#64748b] mx-auto mb-3" />
            <p className="text-[#94A3B8]">Aucun paiement enregistre.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {payments.map((p) => {
              const cfg = paymentStatusConfig[p.payment_status] || paymentStatusConfig.initiated;
              const Icon = cfg.icon;
              return (
                <div key={p.id} className="bg-[#0B0F19] border border-[#2A3441] rounded-xl p-4 flex items-center gap-4" data-testid={`payment-${p.id}`}>
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${cfg.bg}`}>
                    <Icon className={`h-5 w-5 ${cfg.color}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="text-white font-medium">
                        {p.amount?.toLocaleString("fr-FR")} {(p.currency || "eur").toUpperCase()}
                      </p>
                      <Badge className={`${cfg.bg} ${cfg.color} border-0 text-xs`}>{cfg.label}</Badge>
                      {p.metadata?.type === "virtual_terminal" && (
                        <Badge className="bg-[#8B5CF6]/10 text-[#8B5CF6] border-0 text-xs">TPE</Badge>
                      )}
                    </div>
                    <p className="text-xs text-[#64748b] mt-0.5">
                      {p.metadata?.plan_name || p.metadata?.invoice_number || "Paiement"} - {new Date(p.created_at).toLocaleDateString("fr-FR")}
                    </p>
                  </div>
                  <p className="text-xs text-[#64748b] font-mono hidden md:block">{p.session_id?.slice(0, 20)}...</p>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

export default function ClientDetailPage() {
  const { clientId } = useParams();
  const [searchParams] = useSearchParams();
  const { token } = useAuth();
  const [client, setClient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("info");

  // Handle payment return
  useEffect(() => {
    const paymentStatus = searchParams.get("payment");
    const sessionId = searchParams.get("session_id");
    if (paymentStatus === "success" && sessionId) {
      setActiveTab("payments");
      toast.success("Paiement effectue avec succes !");
      // Poll status to confirm
      const pollStatus = async () => {
        try {
          await axios.get(`${API}/billing/checkout/status/${sessionId}`);
        } catch {
          // silent - webhook may handle it
        }
      };
      pollStatus();
    } else if (paymentStatus === "cancelled") {
      toast.info("Paiement annule.");
      setActiveTab("pricing");
    }
  }, [searchParams]);

  useEffect(() => {
    fetchClient();
  }, [clientId]);

  const fetchClient = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/clients/${clientId}`);
      setClient(res.data);
    } catch {
      toast.error("Erreur chargement client");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-[#0099CC]" />
      </div>
    );
  }

  if (!client) {
    return (
      <div className="text-center py-20">
        <p className="text-[#94A3B8]">Client introuvable.</p>
        <Link to="/app/clients">
          <Button variant="link" className="text-[#0099CC] mt-2">Retour aux clients</Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="client-detail-page">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Link to="/app/clients">
          <Button variant="ghost" size="icon" className="text-[#94A3B8] hover:text-white" data-testid="back-to-clients-btn">
            <ArrowLeft className="h-5 w-5" />
          </Button>
        </Link>
        <div className="flex-1">
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold text-white">{client.company_name}</h1>
            {client.client_number && (
              <span className="text-xs font-mono bg-[#0099CC]/10 text-[#0099CC] px-2 py-0.5 rounded">{client.client_number}</span>
            )}
          </div>
          <p className="text-[#0099CC] text-sm">{client.sector}</p>
        </div>
        <Badge className={
          client.plan === "premium"
            ? "bg-[#F59E0B]/20 text-[#F59E0B] border-0"
            : client.plan === "performance"
            ? "bg-[#0099CC]/20 text-[#0099CC] border-0"
            : "bg-[#64748b]/20 text-[#94A3B8] border-0"
        }>
          {client.plan?.charAt(0).toUpperCase() + client.plan?.slice(1)}
        </Badge>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-[#2A3441] overflow-x-auto" data-testid="client-tabs">
        {TABS.map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
              activeTab === tab.key
                ? "border-[#0099CC] text-[#0099CC]"
                : "border-transparent text-[#94A3B8] hover:text-white"
            }`}
            data-testid={`tab-${tab.key}`}
          >
            <tab.icon className="h-4 w-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {activeTab === "info" && <ClientInfoTab client={client} onClientUpdate={fetchClient} />}
      {activeTab === "vault" && <SecureVault clientId={client.id} />}
      {activeTab === "pricing" && <ClientPricingTab client={client} />}
      {activeTab === "payments" && <ClientPaymentsTab client={client} />}
      {activeTab === "social" && <SocialConnectionsWidget clientId={client.id} token={token} readOnly={false} />}
    </div>
  );
}
